import { t } from '@glory/i18n';
import { AuthLayout, Button, Callout } from '@glory/ui';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';

import { PasskeysSection } from '../features/security/passkeys-section.tsx';
import { TwoFactorSection } from '../features/security/two-factor-section.tsx';
import { authClient } from '../lib/auth-client.ts';
import { isUnauthorized, meQuery, resetSession } from '../lib/session.ts';

export const Route = createFileRoute('/secure')({
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.query({ ...meQuery, staleTime: 0 });
      if (!me.twoFactorRequired) throw redirect({ to: '/today', replace: true });
    } catch (e) {
      if (isUnauthorized(e)) throw redirect({ to: '/login' });
      throw e;
    }
  },
  component: SecurePage,
});

/**
 * Обязательная защита входа: студия требует от админов 2FA, а у пользователя нет ни ключа входа, ни кода из приложения.
 * Остальная платформа закрыта и на сервере; здесь — только настройка защиты и выход.
 */
function SecurePage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const [still, setStill] = useState(false);
  const [busy, setBusy] = useState(false);

  const proceed = async () => {
    setBusy(true);
    const fresh = await queryClient.query({ ...meQuery, staleTime: 0 });
    setBusy(false);
    if (fresh.twoFactorRequired) {
      setStill(true);
      return;
    }
    await router.navigate({ to: '/today', replace: true });
  };
  const logout = async () => {
    await authClient.signOut();
    await resetSession(queryClient);
    await router.navigate({ to: '/login', replace: true });
  };

  return (
    <AuthLayout title={t('security.required.title')} lead={t('security.required.lead')}>
      <div className="flex flex-col gap-4">
        <PasskeysSection />
        <TwoFactorSection enabled={me.twoFactorEnabled} />
        {still ? <Callout>{t('security.required.stillRequired')}</Callout> : null}
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" size="lg" loading={busy} onClick={() => void proceed()}>
            {t('security.required.continue')}
          </Button>
          <Button variant="ghost" size="lg" onClick={() => void logout()}>
            {t('security.required.logout')}
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
